import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type {
  AmendLabReportRequest,
  CreateLabReportRequest,
  LabReportWriteResultDto,
  LabRequestReportsDto,
} from '@/types/lab-results-api';

export const reportsKey = (patientId: string, labRequestId: string) =>
  ['lab-request-reports', patientId, labRequestId] as const;

const path = (patientId: string, labRequestId: string) =>
  `/api/Patients/${patientId}/LabRequests/${labRequestId}/Reports`;

/** GET the tests and saved reports of one lab request. */
export function useLabRequestReports(
  patientId: string,
  labRequestId: string,
  enabled = true,
  options?: { staleTime?: number },
) {
  return useQuery<LabRequestReportsDto>({
    queryKey: reportsKey(patientId, labRequestId),
    queryFn: () => get<LabRequestReportsDto>(path(patientId, labRequestId)),
    enabled: enabled && !!patientId && !!labRequestId,
    staleTime: options?.staleTime,
  });
}

/** POST a discrete-result report. Callers await `mutateAsync`, then post any event. */
export function useCreateLabReport(patientId: string, labRequestId: string) {
  const qc = useQueryClient();
  return useMutation<LabReportWriteResultDto, Error, CreateLabReportRequest>({
    mutationFn: (body) => post<LabReportWriteResultDto>(path(patientId, labRequestId), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lab-orders'] });
      void qc.invalidateQueries({ queryKey: reportsKey(patientId, labRequestId) });
    },
  });
}

/**
 * Amend the current version of a report (PROD-3204, PM slice 5): POST the complete next version and a reason. There is
 * no If-Match: the server answers 409 when `labReportId` is no longer the current version.
 */
export function useAmendLabReport(patientId: string, labRequestId: string) {
  const qc = useQueryClient();
  return useMutation<LabReportWriteResultDto, Error, { labReportId: string; body: AmendLabReportRequest }>({
    mutationFn: ({ labReportId, body }) =>
      post<LabReportWriteResultDto>(`${path(patientId, labRequestId)}/${labReportId}/Amend`, body),
    meta: { handledInline: true },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: reportsKey(patientId, labRequestId) });
    },
  });
}
